import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/lib/lang";
import { LogoMark } from "./Logo";
import { MaskLines, ease, goTo } from "./motion";

const Badge = () => (
  <div className="relative h-28 w-28 md:h-36 md:w-36" dir="ltr">
    <motion.svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full" animate={{ rotate: 360 }} transition={{ duration: 36, ease: "linear", repeat: Infinity }}>
      <defs><path id="circ" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" /></defs>
      <text fontSize="8.6" letterSpacing="2.2" fill="#FFFFFF">
        <textPath href="#circ" textLength="286" lengthAdjust="spacing">OGEN HOSPITALITY · PROPERTY NO. 1 · 2027 · </textPath>
      </text>
    </motion.svg>
    <div className="absolute inset-0 grid place-items-center">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--brass-deep)] shadow-[0_10px_24px_-8px_rgba(14,130,134,0.6)]"><LogoMark className="h-7 w-7 text-white" /></div>
    </div>
  </div>
);

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-[#0C3440] pt-[110px] pb-24 md:pt-[130px] md:pb-32 lg:min-h-[100svh]" data-testid="hero-section">
      <motion.div style={{ scale: videoScale, y: videoY }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.8, ease }} className="absolute inset-0" aria-hidden="true">
        <video src="/video/hero-loop.mp4" poster="/img/hero-poster.jpg" autoPlay muted loop playsInline preload="auto" className="h-full w-full object-cover" data-testid="hero-video" />
      </motion.div>
      <div className="absolute inset-0 bg-[linear-gradient(100deg,rgba(8,40,50,0.88)_0%,rgba(8,40,50,0.62)_45%,rgba(8,40,50,0.25)_100%)] rtl:bg-[linear-gradient(-100deg,rgba(8,40,50,0.88)_0%,rgba(8,40,50,0.62)_45%,rgba(8,40,50,0.25)_100%)]" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0C3440] to-transparent" aria-hidden="true" />
      <svg className="pointer-events-none absolute -top-40 end-[-20%] h-[900px] w-[900px] text-[#7FD9D6] opacity-[0.22]" viewBox="0 0 400 400" fill="none" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => <path key={i} d={`M${60 + i * 30} 400V${200 + i * 0}a${140 - i * 30} ${140 - i * 30} 0 0 1 ${280 - i * 60} 0v200`} stroke="currentColor" />)}
      </svg>
      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-5 md:px-10 lg:grid-cols-12 lg:gap-10">
        <motion.div style={{ y: titleY }} className="lg:col-span-9 flex flex-col justify-center lg:min-h-[calc(100svh-260px)]">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }} className="mb-8 flex items-center gap-3 text-[12px] uppercase tracking-[0.3em] text-[#7FD9D6]" data-testid="hero-eyebrow">
            <span className="h-px w-10 bg-[#7FD9D6]" />{t.hero.eyebrow}
          </motion.p>
          <h1 key={lang} className="font-display text-white leading-[0.92] tracking-[-0.02em] text-[clamp(3.4rem,10.5vw,10rem)] lg:text-[clamp(4rem,8.2vw,9.6rem)]" data-testid="hero-title">
            <MaskLines lines={t.hero.lines} onLoad delay={0.35} lineClass={(i) => (i === 1 ? "italic text-[#7FD9D6] ps-[0.6em] lg:ps-[1.1em]" : "")} />
          </h1>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease, delay: 1.1 }} className="mt-10 max-w-xl">
            <p className="text-[15px] md:text-base leading-relaxed text-white/85" data-testid="hero-intro">{t.hero.intro}</p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button onClick={() => goTo("contact")} className="btn-ink" data-testid="hero-cta-viewing">{t.nav.cta}</button>
              <button onClick={() => goTo("property")} className="btn-ghost !bg-white/10 !text-white !border-white/40 hover:!bg-white/20" data-testid="hero-cta-explore">
                {t.hero.explore}<ArrowDown className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, ease, delay: 1.3 }} className="hidden lg:flex lg:col-span-3 items-end justify-end" data-testid="hero-badge"><Badge /></motion.div>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 overflow-hidden text-[#7FD9D6]" aria-hidden="true" dir="ltr">
        {[["animate-wave-slow", 0.35, 10], ["animate-wave", 0.55, 0]].map(([cls, op, dy]) => (
          <svg key={cls} className={`absolute bottom-0 h-16 w-[200%] ${cls}`} style={{ opacity: op, transform: `translateY(${dy}px)` }} viewBox="0 0 2400 64" preserveAspectRatio="none" fill="none">
            <path d="M0 32 Q 150 8 300 32 T 600 32 T 900 32 T 1200 32 T 1500 32 T 1800 32 T 2100 32 T 2400 32" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        ))}
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }} className="absolute bottom-6 start-1/2 hidden -translate-x-1/2 md:flex flex-col items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/80 rtl:translate-x-1/2">
        {t.hero.scroll}
        <span className="relative h-10 w-px overflow-hidden bg-white/25"><span className="absolute inset-x-0 top-0 h-1/2 bg-[#7FD9D6] animate-scrollcue" /></span>
      </motion.div>
    </section>
  );
};
